import { Space, SubText, WarningAlert } from '@procurify/ui'
import { getIn, useFormikContext } from 'formik'

import { useProcurifyIntl } from '@webapp/hooks'
import {
  BudgetCustomFieldCondition,
  type IBudgetCustomFieldConditionValue,
} from '@webapp/platform/budgets'
import messages from './messages'
import { type BudgetCategoryFormFormSchema } from '../../types'

export const BudgetCategoryFormCustomFieldConditions = () => {
  const intl = useProcurifyIntl()
  const { values, errors, touched, setFieldValue, setFieldTouched } =
    useFormikContext<BudgetCategoryFormFormSchema>()

  // With no filter set, Department carries the "at least one filter" error.
  const filterRequiredError: string | undefined = getIn(errors, 'departments')

  const renderCustomFieldCondition = (
    customFieldCondition: IBudgetCustomFieldConditionValue,
    index: number
  ) => {
    const name = `customFields.${index}`
    const isTouched = Boolean(getIn(touched, name))
    const valuesError: string | undefined = isTouched
      ? getIn(errors, `${name}.values`)
      : undefined
    // custom_fields is keyed by field id, so a field can back only one row.
    const unavailableFieldIds = values.customFields
      .filter(
        (otherCondition, otherIndex) =>
          otherIndex !== index && otherCondition.field
      )
      .map((otherCondition) => otherCondition.field.id)
    const fieldLabel = intl.formatMessage(messages.fieldLabel, {
      number: index + 1,
    })

    return (
      <BudgetCustomFieldCondition
        key={name}
        id={`customFields-${index}`}
        value={customFieldCondition}
        onChange={(nextCustomFieldCondition) => {
          setFieldValue(name, nextCustomFieldCondition)
          setFieldTouched(name, true, false)
        }}
        fieldLabel={
          index === 0 ? (
            <>
              {fieldLabel}{' '}
              <SubText inline bold={false}>
                {intl.formatMessage(messages.fieldHint)}
              </SubText>
            </>
          ) : (
            fieldLabel
          )
        }
        valuesLabel={intl.formatMessage(messages.valuesLabel)}
        onFieldBlur={() => setFieldTouched(name, true)}
        fieldError={index === 0 && isTouched ? filterRequiredError : undefined}
        valuesError={valuesError}
        excludedFieldIds={unavailableFieldIds}
      />
    )
  }

  return (
    <Space align='stretch'>
      {values.customFields.map(renderCustomFieldCondition)}
      <WarningAlert>
        <SubText>{intl.formatMessage(messages.warning)}</SubText>
      </WarningAlert>
    </Space>
  )
}
